import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { UserPlus, Repeat, UserX, Loader2, X, MapPin, Globe2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { friendlyError } from "@/lib/friendlyError";
import RevenueHeatMap from "@/components/admin/RevenueHeatMap";

/**
 * The Pulse — story-style dashboard for HQ Command.
 * Three streams (Instagram-inspired circular tiles) sit above an interactive
 * world revenue heat map with multi-level drill-down.
 */
export default function PulseTab({ registerRefresh }) {
  const [stories, setStories] = useState(null);
  const [reasons, setReasons] = useState(null);
  const [loading, setLoading] = useState(true);
  const [churnUserId, setChurnUserId] = useState(null);

  const fetchStories = async () => {
    try {
      const [sRes, rRes] = await Promise.allSettled([
        axios.get(`${API}/admin/pulse/stories?hours=24`),
        axios.get(`${API}/admin/pulse/churn-reasons?window_days=30`),
      ]);
      if (sRes.status === "fulfilled") setStories(sRes.value.data);
      if (rRes.status === "fulfilled") setReasons(rRes.value.data);
      if (sRes.status === "rejected") {
        toast.error(friendlyError(sRes.reason, "Failed to load Pulse stories"));
      }
    } catch (err) {
      toast.error(friendlyError(err, "Failed to load Pulse stories"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
    registerRefresh?.(() => fetchStories);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading Pulse…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pulse-dashboard">
      {/* ---- STORY-STYLE THREE STREAMS ---- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StoryStream
          testId="pulse-stream-new"
          label="New"
          sublabel={`${stories.new_signups_count} today`}
          accent="from-emerald-500 to-teal-500"
          icon={<UserPlus className="h-6 w-6" />}
          users={stories.new_signups}
          emptyText="No new signups yet today"
        />
        <StoryStream
          testId="pulse-stream-renewed"
          label="Renewed"
          sublabel={`${stories.renewed_count} paid`}
          accent="from-amber-500 to-orange-500"
          icon={<Repeat className="h-6 w-6" />}
          users={stories.renewed}
          emptyText="No renewals in the last 24h"
        />
        <StoryStream
          testId="pulse-stream-churned"
          label="Churned"
          sublabel={`${stories.churned_count} left`}
          accent="from-rose-500 to-red-500"
          icon={<UserX className="h-6 w-6" />}
          users={stories.churned}
          emptyText="No cancellations — enjoy your coffee ☕"
          onUserClick={(uid) => setChurnUserId(uid)}
        />
      </div>

      {/* ---- CHURN-REASON AGGREGATE ---- */}
      {reasons && <ChurnReasonsCard data={reasons} />}

      {/* ---- REVENUE HEAT MAP ---- */}
      <RevenueHeatMap />

      {/* ---- CHURN PROFILE MODAL ---- */}
      {churnUserId && (
        <ChurnProfileModal userId={churnUserId} onClose={() => setChurnUserId(null)} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ChurnReasonsCard — Pulse widget aggregating cancellation survey answers   */
/* -------------------------------------------------------------------------- */
const REASON_ACCENT = {
  too_expensive:    "bg-amber-500",
  missing_feature:  "bg-cyan-400",
  too_complicated:  "bg-rose-400",
  other:            "bg-slate-500",
};

function ChurnReasonsCard({ data }) {
  const buckets = data?.buckets || [];
  const total = data?.total || 0;
  const topBucket = [...buckets].sort((a, b) => b.count - a.count)[0];

  return (
    <div
      className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5"
      data-testid="pulse-churn-reasons-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[0.65rem] uppercase tracking-[0.2em] text-rose-400 font-mono mb-1">
            Why they're leaving · 30-day window
          </div>
          <div className="text-xl font-bold text-white">
            {total === 0 ? "No cancellations in the last 30 days" : (
              <>
                <span className="tabular-nums">{total}</span>{" "}
                <span className="text-slate-400 font-normal text-sm">cancellation{total === 1 ? "" : "s"}</span>
              </>
            )}
          </div>
          {total > 0 && topBucket?.count > 0 && (
            <div className="text-xs text-slate-500 mt-1">
              Top reason: <span className="text-white font-medium">{topBucket.label}</span>{" "}
              ({topBucket.pct}%)
            </div>
          )}
        </div>
      </div>

      {total > 0 && (
        <>
          {/* Stacked bar */}
          <div className="h-3 w-full rounded-full overflow-hidden flex bg-slate-800 mb-4" data-testid="pulse-churn-reasons-bar">
            {buckets.map((b) =>
              b.count > 0 ? (
                <div
                  key={b.key}
                  className={REASON_ACCENT[b.key] || "bg-slate-500"}
                  style={{ width: `${b.pct}%` }}
                  title={`${b.label}: ${b.count} (${b.pct}%)`}
                  data-testid={`pulse-churn-bar-${b.key}`}
                />
              ) : null
            )}
          </div>

          {/* Legend */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {buckets.map((b) => (
              <div
                key={b.key}
                className="rounded-lg bg-slate-950/40 border border-slate-800 px-3 py-2"
                data-testid={`pulse-churn-reason-${b.key}`}
              >
                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  <span className={`w-2 h-2 rounded-full ${REASON_ACCENT[b.key] || "bg-slate-500"}`} />
                  <span className="truncate">{b.label}</span>
                </div>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-xl font-bold text-white tabular-nums">{b.count}</span>
                  <span className="text-xs text-slate-500 tabular-nums">{b.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  StoryStream — Instagram-style circular tile stack                         */
/* -------------------------------------------------------------------------- */
function StoryStream({ testId, label, sublabel, accent, icon, users, emptyText, onUserClick }) {
  return (
    <div
      className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 space-y-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${accent} flex items-center justify-center text-slate-950`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-lg font-bold text-white">{label}</div>
          <div className="text-xs text-slate-400">{sublabel}</div>
        </div>
      </div>

      {users.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-4 text-center">{emptyText}</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {users.map((u) => {
            const initials = (u.display || "?")
              .split(" ").filter(Boolean).slice(0, 2)
              .map((w) => w[0]?.toUpperCase()).join("") || "?";
            const clickable = !!onUserClick;
            return (
              <button
                key={u.user_id}
                onClick={() => clickable && onUserClick(u.user_id)}
                disabled={!clickable}
                className={`flex-none w-20 flex flex-col items-center gap-1 group ${clickable ? "cursor-pointer" : "cursor-default"}`}
                data-testid={`${testId}-user-${u.user_id}`}
              >
                <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${accent} p-[2px] ${clickable ? "group-hover:scale-105 transition-transform" : ""}`}>
                  <div className="w-full h-full rounded-full bg-slate-950 flex items-center justify-center text-white text-sm font-bold">
                    {initials}
                  </div>
                </div>
                <span className="text-[10px] text-slate-300 truncate w-full text-center">{u.display}</span>
                {u.country && <span className="text-[9px] text-slate-500">{u.country}</span>}
                {u.detail && (
                  <span className="text-[9px] text-slate-400 truncate w-full text-center">{u.detail}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ChurnProfileModal — deep-dive on one churned user                         */
/* -------------------------------------------------------------------------- */
function ChurnProfileModal({ userId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/pulse/churn/${userId}`);
        setDetail(res.data);
      } catch (err) {
        toast.error(friendlyError(err, "Failed to load churn profile"));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg bg-slate-950 border-slate-800 text-slate-100"
        data-testid="churn-profile-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-rose-400" />
            Churn Profile
          </DialogTitle>
        </DialogHeader>

        {loading || !detail ? (
          <div className="py-8 text-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-xl font-bold text-white">{detail.display}</div>
              <div className="text-sm text-slate-400">{detail.email}</div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <MetaBox label="Country" value={detail.country || "—"} />
              <MetaBox label="Region" value={detail.region || "—"} />
              <MetaBox label="Tier at churn" value={detail.tier_at_churn || "—"} />
              <MetaBox label="Recipe count" value={detail.recipe_count ?? "—"} />
              <MetaBox label="AI translations (this mo.)" value={detail.ai_translation_count_this_month ?? "—"} />
              <MetaBox label="Lifetime paid cycles" value={detail.lifetime_paid_cycles} />
              <MetaBox label="Lifetime revenue" value={detail.lifetime_revenue_display} />
              <MetaBox label="Joined" value={detail.joined_at || "—"} />
            </div>

            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4">
              <div className="text-xs uppercase text-rose-300 tracking-wider mb-1">
                Exit Reason
              </div>
              <div className="text-sm text-slate-100" data-testid="churn-exit-reason">
                {detail.exit_reason || "— not provided —"}
              </div>
              {detail.additional_notes && (
                <div className="text-xs text-slate-400 mt-2 border-t border-rose-500/20 pt-2 whitespace-pre-wrap">
                  {detail.additional_notes}
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-2">
                Churned {detail.churned_at || "—"}
              </div>
            </div>

            <div className="flex justify-end">
              <Button variant="ghost" onClick={onClose} data-testid="churn-modal-close">
                <X className="h-4 w-4 mr-2" /> Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MetaBox({ label, value }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-900/40 p-2">
      <div className="text-[10px] uppercase text-slate-500 tracking-wider">{label}</div>
      <div className="text-sm text-white truncate">{value}</div>
    </div>
  );
}

export { ChurnProfileModal };
